import {
  Box,
  useTheme,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";

import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useRef } from "react";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useAuth } from "../../../contexts/AuthContext";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import Papa from "papaparse";
import {
  gridClasses,
  useGridApiRef,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { UploadFileRounded, FileDownloadRounded } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import RestoreIcon from "@mui/icons-material/RestoreRounded";
//import { LoadingButton } from '@mui/lab';
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/SaveRounded";
import { darken } from "@mui/material/styles";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";
import DataGridResponsive from "components/DataGridResponsive";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import dayjs from "dayjs";
import { getProjectNumber } from "helpers/ServiceHelpers";
import { loadExcelJS } from "helpers/loadExcelJS";
import JSZip from "jszip";
import Menu from "@mui/material/Menu";
import { usePageTitle } from "hooks/usePageTitle";
import { generateInvoicePdfBlob } from "scenes/management/invoices/invoicePdf";
import DetailedLogsDocument, {
  DETAILED_LOGS_HEADERS,
} from "./DetailedLogsDocument";

/**
 * Language Normalization Mapping Configuration
 *
 * This array defines how language names from OPI logs should be normalized.
 * Each mapping has:
 *   - from: A regex pattern to match the original language name (use /^exact$/i for exact match)
 *   - to: The target normalized name that should be used
 *
 * The system will:
 *   1. Check each language name against these patterns in order
 *   2. Apply the first matching transformation
 *   3. Use this for display, ID resolution, and data compression
 *
 * To add a new mapping:
 *   { from: /^Original Name$/i, to: "Target Name" },
 *
 * Examples:
 *   - Exact match: { from: /^Persian$/i, to: "Dari" }
 *   - Starts with: { from: /^dari\b/i, to: "Dari" }
 *   - Pattern match: { from: /^Chin - Falam$/i, to: "Falam Chin" }
 */
const LANGUAGE_NORMALIZATION_MAP = [
  { from: /^dari\b/i, to: "Dari" },
  { from: /^farsi,?\s*iranian$/i, to: "Farsi" }, // Handle "Farsi, Iranian" or "Farsi Iranian"
  { from: /^farsi\b/i, to: "Farsi" },
  { from: /^portuguese\b/i, to: "Portuguese" },
  { from: /^kiswahili$/i, to: "Swahili" },
  { from: /^Lebanese Arabic$/i, to: "Arabic" },
  { from: /^Chin - Falam$/i, to: "Falam Chin" },
  { from: /^Persian$/i, to: "Dari" },
  { from: /^Chinese \(Mandarin\)$/i, to: "Mandarin" },
  // Add more mappings below as needed:
  // { from: /^Original Name$/i, to: "Target Name" },
];

// Detailed Logs PDF export tuning — mirrors the invoice export flow
// (InvoiceExport.jsx); scale matches InvoiceExport's EXPORT_SCALE. Each
// document is waited on with bounded polling instead of fixed delays: first
// until the off-screen render carries the current client's marker (React has
// committed that client's document — capturing a stale/previous document is
// what produces wrong PDFs), then until the CE logo images have loaded.
const DETAILED_LOGS_EXPORT_SCALE = 8;
const DETAILED_LOGS_POLL_INTERVAL_MS = 100; // poll tick for both waits below
const DETAILED_LOGS_MOUNT_TIMEOUT_MS = 5000; // render-commit wait ceiling
const DETAILED_LOGS_IMAGE_TIMEOUT_MS = 3000; // logo-load wait ceiling

const InterpretationOPIUpload = () => {
  usePageTitle("OPI Upload");

  const { currentUser } = useAuth();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const apiRef = useGridApiRef();

  const [hasUnsavedRows, setHasUnsavedRows] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const unsavedChangesRef = React.useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });

  const theme = useTheme();
  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    openDialog,
    closeDialog,
    showSnackbar,
  } = useStateContext();
  const [selectedFile, setSelectedFile] = useState(null);

  const [isCSVProcessing, setIsCSVProcessing] = useState(false);
  const [csvRawData, setCsvRawData] = useState([]);
  const [csvCleanData, setCsvCleanData] = useState([]);
  const [csvCompressedData, setCsvCompressedData] = useState([]);
  const [errorsList, setErrorsList] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processPercent, setProcessPercent] = useState(0);
  const [processDone, setProcessDone] = useState(false);
  const [downloadDetailedLogs, setDownloadDetailedLogs] = useState(true);

  // Off-screen Detailed Logs document (one client at a time) for the
  // invoice-styled PDF export. The ref sits on the document root because
  // generateInvoicePdfBlob captures the ref's direct children as PDF pages.
  const detailedLogsRef = useRef(null);
  const [detailedLogsDoc, setDetailedLogsDoc] = useState(null);

  // Detailed Logs ZIP export state shown on the process-complete screen.
  // `pdfExportStatus` doubles as the "a download is running" flag (button
  // spinner, disabled controls); `pdfExportError` keeps the last failure
  // visible next to the button so the overlay is never closed silently.
  const [pdfExportStatus, setPdfExportStatus] = useState(null);
  const [pdfExportError, setPdfExportError] = useState(null);

  // Reporting date inferred from Raw Logs (last day of the dominant month)
  const [date, setDate] = useState(null);
  const [projectNumber, setProjectNumber] = useState(null);

  const [dataRows, setDataRows] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);

  const sheets = ["Raw Logs", "Clean Logs", "Compressed Logs"];

  const [sheet, setSheet] = useState(sheets[0]);
  const columnVisibilityModel = React.useMemo(
    () => ({
      actions: sheet === "Raw Logs",
      LanguageID: false,
      ClientID: false,
    }),
    [sheet],
  );

  // Helper function to normalize language names using the mapping
  const normalizeLanguageName = (languageName) => {
    if (languageName === null) return null;
    let name = String(languageName).trim();
    if (!name) return null;

    // Apply each mapping rule in order
    for (const mapping of LANGUAGE_NORMALIZATION_MAP) {
      if (mapping.from.test(name)) {
        return mapping.to;
      }
    }

    // Return original if no mapping matched
    return name;
  };

  // Helper function to split languages while protecting multi-part language names
  const splitAndNormalizeLanguages = (langsRaw) => {
    if (!langsRaw) return [];

    let processedLangs = String(langsRaw);

    // Replace known multi-part languages with temporary placeholders
    const protectedLanguages = [];
    LANGUAGE_NORMALIZATION_MAP.forEach((mapping, index) => {
      const match = processedLangs.match(mapping.from);
      if (match) {
        const placeholder = `__LANG_${index}__`;
        protectedLanguages[index] = match[0];
        processedLangs = processedLangs.replace(mapping.from, placeholder);
      }
    });

    // Split on delimiters
    const parts = _splitLanguagesOutsideParens(processedLangs);

    // Restore protected languages
    return parts.map((p) => {
      const placeholderMatch = p.match(/__LANG_(\d+)__/);
      return placeholderMatch ? protectedLanguages[placeholderMatch[1]] : p;
    });
  };

  const columns = React.useMemo(() => {
    return [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<RestoreIcon />}
              label="Discard changes"
              disabled={unsavedChangesRef.current.unsavedRows[id] === undefined}
              onClick={() => {
                apiRef.current?.updateRows([
                  unsavedChangesRef.current.rowsBeforeChange[id],
                ]);
                delete unsavedChangesRef.current.rowsBeforeChange[id];
                delete unsavedChangesRef.current.unsavedRows[id];
                setHasUnsavedRows(
                  Object.keys(unsavedChangesRef.current.unsavedRows).length > 0,
                );
              }}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                unsavedChangesRef.current.unsavedRows[id] = {
                  ...row,
                  _action: "delete",
                };
                if (!unsavedChangesRef.current.rowsBeforeChange[id]) {
                  unsavedChangesRef.current.rowsBeforeChange[id] = row;
                }
                setHasUnsavedRows(true);
                apiRef.current?.updateRows([row]); // to trigger row render
              }}
            />,
          ];
        },
      },
      ...dataColumns,
    ];
  }, [dataColumns, unsavedChangesRef, apiRef]);

  const processRowUpdate = React.useCallback((newRow, oldRow) => {
    const rowId = newRow.id;

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    setHasUnsavedRows(true);
    return newRow;
  }, []);

  const discardChanges = React.useCallback(() => {
    setHasUnsavedRows(false);
    Object.values(unsavedChangesRef.current.rowsBeforeChange).forEach((row) => {
      apiRef.current?.updateRows([row]);
    });
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };
  }, [apiRef]);

  const saveChanges = async () => {
    try {
      // Persist updates in the database
      setIsSaving(true);
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      const unsaved = unsavedChangesRef.current.unsavedRows;
      const changedRows = Object.values(unsaved);
      const rowsToDelete = changedRows.filter(
        (row) => row._action === "delete",
      );
      const rowsToUpsert = changedRows.filter(
        (row) => row._action !== "delete",
      );

      // Reflect changes back to csvRawData only when editing Raw Logs
      if (sheet === "Raw Logs" && changedRows.length > 0) {
        setCsvRawData((prev) => {
          const next = [...prev];

          // Apply edits/updates
          rowsToUpsert.forEach((row) => {
            const idx = Number(row.id) - 1;
            if (Number.isInteger(idx) && idx >= 0 && idx < next.length) {
              const { id, _action, ...rest } = row;
              next[idx] = { ...next[idx], ...rest };
            }
          });

          // Apply deletions in descending order of index to avoid reindexing issues
          const deleteIdxs = rowsToDelete
            .map((row) => Number(row.id) - 1)
            .filter(
              (idx) => Number.isInteger(idx) && idx >= 0 && idx < next.length,
            )
            .sort((a, b) => b - a);

          deleteIdxs.forEach((idx) => {
            next.splice(idx, 1);
          });

          const withIDs = annotateRowsWithIDs(next);
          const cleaned = cleanCSVData(withIDs);
          const compressed = compressCSVData(cleaned);

          setCsvCleanData(cleaned);
          setCsvCompressedData(compressed);
          setErrorsList(computeCompressedErrors(compressed));
          setDate(computeReportingDate(withIDs));

          return withIDs;
        });
      }

      // Update the grid UI for deletions immediately
      if (rowsToDelete.length > 0) {
        rowsToDelete.forEach((row) => {
          apiRef.current?.updateRows([row]);
        });
      }

      setIsSaving(false);

      setHasUnsavedRows(false);
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
      };
    } catch (error) {
      setIsSaving(false);
    }
  };

  const getRowClassName = React.useCallback(({ id }) => {
    const unsavedRow = unsavedChangesRef.current.unsavedRows[id];
    if (unsavedRow) {
      if (unsavedRow._action === "delete") {
        return "row--removed";
      }
      return "row--edited";
    }
    return "";
  }, []);

  const handleChange = (event) => {
    setSheet(event.target.value);
  };

  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const processCSV = (file) => {
    Papa.parse(file, {
      header: true, // If your CSV has a header row
      dynamicTyping: true, // Automatically converts numbers/booleans
      skipEmptyLines: true,
      complete: (results) => {
        const sanitized = sanitizeRows(results.data);
        const withIDs = annotateRowsWithIDs(sanitized);
        const cleaned = cleanCSVData(withIDs);
        const compressed = compressCSVData(cleaned);

        setRowsAndColumns(withIDs);
        setCsvRawData(withIDs);

        setCsvCleanData(cleaned);
        setCsvCompressedData(compressed);
        setErrorsList(computeCompressedErrors(compressed));
        setDate(computeReportingDate(withIDs));

        setIsCSVProcessing(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error.message);
      },
    });
  };

  function cleanCSVData(data) {
    if (!Array.isArray(data) || data.length === 0) return [];

    const keep = new Set([
      "Requestor",
      "Account Name",
      "Date/Time",
      "Language",
      "Service Type",
      "Client Billable Duration",
      "Account Charge",
      "Account Conference Charge",
      "From Number",
      "Requestor Device Info",
      "Other Participants",
      "LanguageID",
      "ClientID",
    ]);

    const parseDurationToSeconds = (value) => {
      if (value === null) return 0;
      if (typeof value === "number" && Number.isFinite(value)) return value; // already numeric seconds
      if (typeof value === "string") {
        const str = value.trim();
        if (!str) return 0;
        const parts = str.split(":").map((p) => Number(p));
        if (parts.some((n) => Number.isNaN(n))) return 0;
        let h = 0,
          m = 0,
          s = 0;
        if (parts.length === 3) [h, m, s] = parts;
        else if (parts.length === 2) {
          [m, s] = parts;
        } else if (parts.length === 1) {
          [s] = parts;
        }
        return h * 3600 + m * 60 + s;
      }
      return 0;
    };

    const formatSecondsToHMS = (secs) => {
      const total = Math.max(0, Math.floor(secs));
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    };

    const THRESHOLD = 5 * 60; // 00:05:00 in seconds

    const cleaned = data.map((row) => {
      const filtered = {};

      // Copy only the kept columns; ensure all exist
      keep.forEach((k) => {
        filtered[k] = row?.[k] ?? null;
      });

      // Normalize Language 2 display using the language mapping
      (() => {
        const langsRaw = row?.["Language 2"];
        const parts = splitAndNormalizeLanguages(langsRaw);

        const normalized = [];
        const seen = new Set();
        parts.forEach((p) => {
          const normalizedLang = normalizeLanguageName(p);
          if (!normalizedLang) return;

          const key = normalizedLang.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            normalized.push(normalizedLang);
          }
        });
        filtered["Language"] = normalized.join(", ");
      })();

      // Original duration for sorting
      const origSec = parseDurationToSeconds(row?.["Client Billable Duration"]);

      // Clamp display value to at least threshold, but keep sort on original
      filtered["Client Billable Duration"] =
        origSec < THRESHOLD
          ? formatSecondsToHMS(THRESHOLD)
          : formatSecondsToHMS(origSec);

      // Compute Account Charge using clamped duration:
      // Account Charge = [Client Billable Duration (days)] * 1440 * 1.25 + [Account Conference Charge]
      // Since we operate in seconds, convert to minutes then apply rate.
      const durationForChargeSec = Math.max(THRESHOLD, origSec);
      const durationMinutes = durationForChargeSec / 60;
      const parseMoney = (v) => {
        if (v === null) return 0;
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string") {
          const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
          return Number.isNaN(n) ? 0 : n;
        }
        return 0;
      };
      const confCharge = parseMoney(filtered["Account Conference Charge"]);
      const accountCharge = durationMinutes * 1.25 + confCharge;
      filtered["Account Charge"] = Number(accountCharge.toFixed(2));

      // Temp prop for sorting based on original duration
      filtered.__sortSec = origSec;
      return filtered;
    });

    // Sort by original duration ascending
    cleaned.sort((a, b) => a.__sortSec - b.__sortSec);

    // Remove temp helper and return
    return cleaned.map(({ __sortSec, ...rest }) => rest);
  }

  function sanitizeRows(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.filter((r) => {
      if (r === null || typeof r !== "object") return false;
      const vals = Object.values(r);
      if (vals.length === 0) return false;
      return vals.some((v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === "string") return v.trim() !== "";
        return true;
      });
    });
  }

  // Compute the reporting date from Raw Logs' "Date/Time" column.
  // It picks the month that appears in at least 90% of rows (by YYYY-MM),
  // then returns a dayjs object set to the last day of that month. Otherwise returns null.
  function computeReportingDate(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const counts = new Map();
    let totalValid = 0;

    rows.forEach((r) => {
      const raw = r?.["Date/Time"];
      if (raw === null || raw === "") return;
      let d = dayjs(raw);
      if (!d.isValid()) {
        d = dayjs.tz(String(raw));
      }
      if (!d.isValid()) return;
      const key = d.format("YYYY-MM");
      counts.set(key, (counts.get(key) || 0) + 1);
      totalValid += 1;
    });

    if (totalValid === 0) return null;

    let bestKey = null;
    let bestCount = 0;
    counts.forEach((cnt, key) => {
      if (cnt > bestCount) {
        bestCount = cnt;
        bestKey = key;
      }
    });

    const ratio = bestCount / totalValid;
    if (ratio < 0.9 || !bestKey) return null;

    // Construct YYYY-MM-01 then take endOf('month')
    const d = dayjs(`${bestKey}-01`).endOf("month");

    const finalDate = d.isValid() ? d : null;

    setProjectNumber(getProjectNumber(finalDate, overlayData.Interpretations));

    return finalDate;
  }

  // Helper functions for resolving IDs from overlayData
  function _canonicalKey(v) {
    if (v === null) return "";
    let s = String(v);
    s = s.normalize ? s.normalize("NFKC") : s;
    s = s.replace(/\u00A0/g, " ");
    s = s.trim().replace(/\s+/g, " ");
    s = s.replace(/^['"]+|['"]+$/g, "");
    return s.toLowerCase();
  }
  function _canonicalLangName(v) {
    if (v === null) return "";
    const normalized = normalizeLanguageName(v);
    return normalized ? _canonicalKey(normalized) : "";
  }
  function _splitLanguagesOutsideParens(input) {
    if (input === null) return [];
    const str = String(input);
    const out = [];
    let cur = "";
    let depth = 0;
    for (let i = 0; i < str.length; i++) {
      const ch = str[i];
      if (ch === "(") {
        depth++;
        cur += ch;
        continue;
      }
      if (ch === ")") {
        depth = Math.max(0, depth - 1);
        cur += ch;
        continue;
      }
      if ((ch === "|" || ch === ",") && depth === 0) {
        if (cur.trim()) out.push(cur.trim());
        cur = "";
        continue;
      }
      cur += ch;
    }
    if (cur.trim()) out.push(cur.trim());
    return out;
  }
  function annotateRowsWithIDs(rows) {
    const langMap = new Map();
    const clientMap = new Map();
    if (overlayData?.Languages) {
      overlayData.Languages.forEach((l) => {
        const key = _canonicalLangName(l?.Name);
        if (key) langMap.set(key, l?.id ?? null);
      });
    }
    if (overlayData?.Clients) {
      overlayData.Clients.forEach((c) => {
        const key = _canonicalKey(c?.Account);
        if (key) clientMap.set(key, c?.id ?? null);
      });
    }
    return Array.isArray(rows)
      ? rows.map((row) => {
          const langsRaw = row?.["Language 2"];
          const parts = splitAndNormalizeLanguages(langsRaw);

          const langIds = [];
          const seen = new Set();
          parts.forEach((p) => {
            const key = _canonicalLangName(p);
            const id = key ? langMap.get(key) : null;
            if (id != null && !seen.has(id)) {
              seen.add(id);
              langIds.push(id);
            }
          });
          let clientKey = _canonicalKey(row?.["Account Name"]);
          if (/^(cws\s+global|cws\s+lancaster)\b/.test(clientKey)) {
            clientKey = _canonicalKey("CWS Global");
          }
          const clientId = clientKey
            ? (clientMap.get(clientKey) ?? null)
            : null;
          return { ...row, LanguageID: langIds, ClientID: clientId };
        })
      : [];
  }

  // Resolve English language ID dynamically instead of hardcoding
  const englishLangId = React.useMemo(() => {
    try {
      const list = overlayData?.Languages || [];
      const match = list.find((l) => {
        const n = l?.Name;
        return typeof n === "string" && n.trim().toLowerCase() === "english";
      });

      return match?.id ?? null;
    } catch (_) {
      return null;
    }
  }, [overlayData]);

  function compressCSVData(data) {
    if (!Array.isArray(data) || data.length === 0) return [];

    // Normalize display language names using the mapping
    const normalizeLang = (v) => {
      return normalizeLanguageName(v);
    };

    const parseMoney = (v) => {
      if (v === null) return 0;
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string") {
        const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
        return Number.isNaN(n) ? 0 : n;
      }
      return 0;
    };

    const normalizeDisplay = (v) => (v === null ? "" : String(v).trim());
    const normalizeKeyString = (v) => {
      if (v === null) return "";
      let s = String(v);
      s = s.normalize ? s.normalize("NFKC") : s;
      s = s.replace(/\u00A0/g, " ");
      s = s.trim().replace(/\s+/g, " ");
      s = s.replace(/^['"]+|['"]+$/g, "");
      return s.toLowerCase();
    };
    const keyOf = (account, requestor) =>
      `${normalizeKeyString(account)}||${normalizeKeyString(requestor)}`;

    // Build a language name -> id map for quick lookup
    const langIdMap = new Map();
    if (overlayData?.Languages) {
      overlayData.Languages.forEach((l) => {
        const key = _canonicalLangName(l?.Name);
        if (key) langIdMap.set(key, l?.id ?? null);
      });
    }

    // Group by Account + Requestor and aggregate charges per language
    const groups = new Map();
    data.forEach((row) => {
      const accountRaw = row?.["Account Name"] ?? "";
      const requestorRaw = row?.["Requestor"] ?? "";
      const key = keyOf(accountRaw, requestorRaw);

      let g = groups.get(key);
      if (!g) {
        g = {
          accountDisplay: normalizeDisplay(accountRaw),
          requestorDisplay: normalizeDisplay(requestorRaw),
          entriesByLang: new Map(), // lang -> { charge, langId, lang }
          totalCharge: 0,
          clientId: null,
        };
        groups.set(key, g);
      }

      const langsRaw = row?.["Language"] ?? row?.["Language 2"];
      let parts = splitAndNormalizeLanguages(langsRaw);
      parts = parts
        .map((p) => normalizeLang(p))
        .filter((x) => x && String(x).trim() !== "");

      // Fallback if nothing parsed
      if (parts.length === 0) parts = ["English"]; // ensure we can allocate the charge

      const charge = parseMoney(row?.["Account Charge"]);
      const perPart = parts.length > 0 ? charge / parts.length : 0;

      parts.forEach((lang) => {
        let entry = g.entriesByLang.get(lang);
        if (!entry) {
          const id =
            langIdMap.get(_canonicalLangName(lang)) ||
            (lang.toLowerCase() === "english" ? englishLangId : null);
          entry = { charge: 0, langId: id, lang };
          g.entriesByLang.set(lang, entry);
        }
        entry.charge += perPart;
      });

      g.totalCharge += charge;
      if (g.clientId === null && row?.["ClientID"] != null) {
        g.clientId = row["ClientID"];
      }
    });

    // Normalize Account Name for display: if it starts with "CWS Global", truncate to just "CWS Global"
    const normalizeAccountForDisplay = (name) => {
      if (name === null) return "";
      const s = String(name).trim();
      return s; // /^CWS\s+Global\b/i.test(s) ? "CWS Global" : s;
    };

    // Build a lookup of OPI minimums by ClientID for quick access
    const clientsMinList = overlayData?.ClientsOPIMinimums || [];
    const minByClientId = new Map();
    clientsMinList.forEach((c) => {
      const idNum = Number(c?.id);
      const rateNum = parseMoney(c?.OPIMinimumRate);
      if (Number.isFinite(idNum) && rateNum > 0) {
        minByClientId.set(idNum, {
          rate: rateNum,
          account: String(c?.Account ?? ""),
        });
      }
    });

    const result = [];
    const presentClientIds = new Set();

    groups.forEach((g) => {
      const fullAccountName = g.accountDisplay;
      const displayAccountName = normalizeAccountForDisplay(fullAccountName);

      const cidNum = Number(g.clientId);
      const minInfo = Number.isFinite(cidNum)
        ? minByClientId.get(cidNum)
        : undefined;

      const baseTotal = Number(g.totalCharge.toFixed(2));
      let uplift = 0;
      let usedMinimum = false;
      if (minInfo) {
        const minRate = Number(minInfo.rate.toFixed(2));
        if (baseTotal < minRate) {
          uplift = minRate - baseTotal;
          usedMinimum = true;
        } else if (Math.abs(baseTotal - minRate) < 1e-9) {
          usedMinimum = true;
        }
      }

      const entries = Array.from(g.entriesByLang.values());
      const sumLangCharges = entries.reduce((acc, e) => acc + e.charge, 0);

      if (entries.length === 0) {
        // No language parsed; collapse to a single English entry
        const langLabel = "English";
        const finalCharge = Number((baseTotal + uplift).toFixed(2));
        // Only include the requestor segment when one exists — an absent
        // requestor must not leave an empty " -  - " gap in the Details.
        const requestorSegment = g.requestorDisplay
          ? ` - ${g.requestorDisplay}`
          : "";
        const details = `${fullAccountName}${requestorSegment} - Monthly On-Demand Phone ${langLabel} Interpretation${
          usedMinimum ? " - Minimum" : ""
        }`;
        result.push({
          "Account Name": displayAccountName,
          Requestor: g.requestorDisplay,
          Language: "English",
          LanguageID: englishLangId,
          ClientID: g.clientId,
          "Account Charge": finalCharge,
          Details: details,
        });
      } else {
        entries.forEach((e) => {
          const share =
            sumLangCharges > 0 ? e.charge / sumLangCharges : 1 / entries.length;
          const finalCharge = Number((e.charge + uplift * share).toFixed(2));
          const langLabel = e.lang || "Unknown";
          // Only include the requestor segment when one exists — an absent
          // requestor must not leave an empty " -  - " gap in the Details.
          const requestorSegment = g.requestorDisplay
            ? ` - ${g.requestorDisplay}`
            : "";
          let details = `${fullAccountName}${requestorSegment} - Monthly On-Demand Phone ${langLabel} Interpretation`;
          if (usedMinimum) details += " - Minimum";

          result.push({
            "Account Name": displayAccountName,
            Requestor: g.requestorDisplay,
            Language: e.lang,
            LanguageID: e.langId,
            ClientID: g.clientId,
            "Account Charge": finalCharge,
            Details: details,
          });
        });
      }

      if (Number.isFinite(cidNum)) presentClientIds.add(cidNum);
    });

    // Add entries for clients that have an OPI minimum but no data rows
    clientsMinList.forEach((c) => {
      const idNum = Number(c?.id);
      const rateNum = parseMoney(c?.OPIMinimumRate);
      if (!Number.isFinite(idNum) || !(rateNum > 0)) return;
      if (presentClientIds.has(idNum)) return;

      const fullAccountName = String(c?.Account ?? "");
      const displayAccountName = normalizeAccountForDisplay(fullAccountName);
      // Only include the requestor segment when one exists — an absent
      // requestor must not leave an empty " -  - " gap in the Details.
      const requestorSegment = displayAccountName
        ? ` - ${displayAccountName}`
        : "";
      result.push({
        "Account Name": displayAccountName,
        Requestor: displayAccountName,
        Language: "English",
        LanguageID: englishLangId,
        ClientID: idNum,
        "Account Charge": Number(rateNum.toFixed(2)),
        Details: `${fullAccountName}${requestorSegment} - Monthly On-Demand Phone Interpretation - Minimum`,
      });
    });

    // Sort by Account Name, Requestor, then Language
    result.sort((a, b) => {
      const aAcc = String(a["Account Name"] ?? "");
      const bAcc = String(b["Account Name"] ?? "");
      const accCmp = aAcc.localeCompare(bAcc, undefined, {
        sensitivity: "base",
      });
      if (accCmp !== 0) return accCmp;
      const aReq = String(a["Requestor"] ?? "");
      const bReq = String(b["Requestor"] ?? "");
      const reqCmp = aReq.localeCompare(bReq, undefined, {
        sensitivity: "base",
      });
      if (reqCmp !== 0) return reqCmp;
      const aLang = String(a["Language"] ?? "");
      const bLang = String(b["Language"] ?? "");
      return aLang.localeCompare(bLang, undefined, { sensitivity: "base" });
    });

    return result;
  }

  function computeCompressedErrors(rows) {
    const errs = [];
    (rows || []).forEach((r) => {
      const lang = String(r?.["Language"] ?? "").trim();
      const langIds = Array.isArray(r?.LanguageID)
        ? r.LanguageID
        : r?.LanguageID != null
          ? [r.LanguageID]
          : [];
      const acc = String(r?.["Account Name"] ?? "").trim();
      const req = String(r?.Requestor ?? "").trim();
      const clientIdNum = Number(r?.ClientID);
      const details = String(r?.Details ?? "").trim();

      if (lang && (!langIds || langIds.length === 0)) {
        errs.push(
          `Missing LanguageID for "${acc}" (${req}): The language "${lang}" isn't added to the list of languages, make sure you add it, or check if the language name is written correctly.`,
        );
      }
      if (acc && (!Number.isFinite(clientIdNum) || clientIdNum <= 0)) {
        errs.push(
          `Missing ClientID for "${acc}" (${req}): The client "${acc}", isn't added to the list of clients, make sure you add it, or check if the client name is written correctly.`,
        );
      }
      if (!details) {
        errs.push(`Missing Details for "${acc}" (${req})`);
      }
    });
    return errs;
  }

  const setRowsAndColumns = React.useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      setDataColumns([]);
      setDataRows([]);
      return;
    }

    const columns = Object.keys(data[0]).map((key) => ({
      field: key,
      headerName: key,
      editable: sheet === "Raw Logs" ? true : false,
      width: getColumnWidth(key),
    }));
    const rows = data.map((item, index) => ({
      id: index + 1, // DataGrid needs unique id
      ...item,
    }));
    setDataColumns(columns);
    setDataRows(rows);
  }, [sheet]);

  function getColumnWidth(key) {
    switch (key) {
      case "Call Id":
        return 150;
      case "Interpreter":
        return 150;
      case "Interpreter Id":
        return 120;
      case "Requestor":
        return 160;
      case "Account Name":
        return 200;
      case "Date/Time":
        return 180;
      case "Language 1":
        return 100;
      case "Language":
        return 160;
      case "Language 2":
        return 120;
      case "Service Type":
        return 100;
      case "Type":
        return 80;
      case "Duration":
        return 100;
      case "Client Billable Duration":
        return 100;
      case "Account Charge":
        return 150;
      case "ClientID":
        return 120;
      case "LanguageID":
        return 160;
      case "Details":
        return 450;
      default:
        return 100;
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsCSVProcessing(true);

    setSelectedFile(file);

    processCSV(file);

    // Reset the input so selecting the same file again triggers onChange
    event.target.value = "";
  };

  // Generate the Detailed Logs PDFs (invoice-styled documents, one per
  // client) and download them as a ZIP. Each document is rendered off-screen
  // from `DetailedLogsDocument` — the same letter sheet (landscape for the
  // wide detail table), header, footer and accents as an invoice page — and
  // captured with the shared invoice PDF pipeline (orientation: "landscape")
  // so the output matches an invoice layout exactly.
  // Returns true when the ZIP was downloaded, false when nothing could be
  // exported (the failure is alerted here AND kept inline via
  // `pdfExportError` so the caller can leave the overlay open).
  const exportDetailedPDFs = async () => {
    const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    // Bounded polling helper — resolves as soon as the predicate passes,
    // throws a descriptive error when the budget runs out.
    const waitFor = async (predicate, timeoutMs, failureMessage) => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        if (predicate()) return;
        await waitMs(DETAILED_LOGS_POLL_INTERVAL_MS);
      }
      throw new Error(failureMessage);
    };
    const fail = (message, err) => {
      console.error("Detailed Logs PDF export failed", err ?? message);
      setPdfExportError(message);
      alert(message);
      return false;
    };

    try {
      setPdfExportError(null);

      // Desired column order for detailed logs
      const detailedHeaders = DETAILED_LOGS_HEADERS;

      // Group Clean Logs by Account Name
      const groupsByClient = new Map();
      (Array.isArray(csvCleanData) ? csvCleanData : []).forEach((row) => {
        let accName = String(row?.["Account Name"] ?? "").trim();
        if (!accName) accName = "Unknown";
        if (!groupsByClient.has(accName)) groupsByClient.set(accName, []);
        groupsByClient.get(accName).push(row);
      });

      // Name mapping for specific clients
      const nameMap = {
        "CWS Global - HQ - Remote Services": "CWS HQ Remote Services",
        "CWS Global - Southwest - Houston": "CWS SW Houston",
        "CWS Global - Southwest - Dallas": "CWS SW Dallas",
      };

      const safe = (s) => s.replace(/[\\/:*?"<>|]/g, "_");
      const zip = new JSZip();
      const nameMapFn = (n) => nameMap[n] || n;

      // Period label for the document header — file-wide, so it is computed
      // once before the client loop (every uploaded file covers a whole
      // month). Primary source: the dominant-month reporting `date` derived
      // from Raw Logs (always rendered as the FULL month). Fallback: the
      // min/max of the Clean Logs "Date/Time" values. Nothing derivable →
      // empty string and the Period row is simply not rendered.
      const periodLabel = (() => {
        if (date?.isValid?.()) {
          const start = date.startOf("month");
          const end = date.endOf("month");
          return `${start.format("MMMM D")} – ${end.format(
            "MMMM D, YYYY",
          )}`;
        }
        let min = null;
        let max = null;
        (Array.isArray(csvCleanData) ? csvCleanData : []).forEach((r) => {
          const raw = r?.["Date/Time"];
          if (raw === null || raw === undefined || raw === "") return;
          let d = dayjs(raw);
          if (!d.isValid()) d = dayjs.tz(String(raw));
          if (!d.isValid()) return;
          if (!min || d.isBefore(min)) min = d;
          if (!max || d.isAfter(max)) max = d;
        });
        if (!min || !max) return "";
        return min.isSame(max, "year")
          ? `${min.format("MMMM D")} – ${max.format("MMMM D, YYYY")}`
          : `${min.format("MMMM D, YYYY")} – ${max.format(
              "MMMM D, YYYY",
            )}`;
      })();

      // One client at a time: mount the document into the hidden off-screen
      // host (state-driven, like InvoiceExport), wait until the committed
      // render provably belongs to this client, capture it page by page,
      // then move on. The host ref sits on the document root —
      // generateInvoicePdfBlob treats its direct children as PDF pages.
      const totalClients = groupsByClient.size;
      let clientIndex = 0;
      let zipFileCount = 0;
      for (const [clientName, rows] of groupsByClient) {
        const mapped = nameMapFn(clientName);
        const data = rows.map((r) => {
          const out = {};
          detailedHeaders.forEach((h) => (out[h] = r?.[h] ?? ""));
          return out;
        });

        clientIndex += 1;
        const clientLabel = `client ${clientIndex}/${totalClients}: ${mapped}`;
        const report = (detail) =>
          setPdfExportStatus(
            `Generating Detailed Logs PDFs… (${clientLabel}${
              detail ? ` — ${detail}` : ""
            })`,
          );
        report();

        setDetailedLogsDoc({ client: mapped, rows: data, period: periodLabel });

        // Bounded poll #1: React must have committed the off-screen document
        // for THIS client (the `data-client` marker) before anything is
        // captured — guards against capturing a stale previous document or
        // an unmounted host.
        await waitFor(
          () => detailedLogsRef.current?.dataset.client === mapped,
          DETAILED_LOGS_MOUNT_TIMEOUT_MS,
          `Detailed Logs document did not render for ${mapped} in time`,
        );

        if (document.fonts?.ready) {
          try {
            await document.fonts.ready;
          } catch (e) {
            // Non-fatal — capture can proceed with the already-loaded fonts.
          }
        }

        const host = detailedLogsRef.current;
        if (!host) throw new Error("Render container missing");

        // Bounded poll #2: the CE logo <img> on every page must finish
        // loading before the capture (an incomplete image renders blank in
        // the PDF). Cached logos are `complete` on the first tick.
        await waitFor(
          () =>
            Array.from(host.querySelectorAll("img")).every(
              (img) => img.complete,
            ),
          DETAILED_LOGS_IMAGE_TIMEOUT_MS,
          `CE logo images did not finish loading for ${mapped}`,
        );

        const blob = await generateInvoicePdfBlob(host, {
          scale: DETAILED_LOGS_EXPORT_SCALE,
          onProgress: (pageStatus) => report(pageStatus),
          // Landscape letter sheet — the detail table has far more columns
          // than rows, so the invoice-style pages are captured wide.
          orientation: "landscape",
          // Pinned to the raster engine: this export stays byte-identical
          // while the invoice flows migrate to the text engine (P2).
          engine: "raster",
        });
        zip.file(`Detailed Logs - ${safe(mapped)}.pdf`, blob);
        zipFileCount += 1;
      }

      // Never download an empty ZIP — nothing to package.
      if (zipFileCount === 0) {
        return fail(
          "No detailed log rows were found to export — process the OPI logs first.",
        );
      }

      setPdfExportStatus("Packaging ZIP…");
      const content = await zip.generateAsync({ type: "blob" });
      const base = selectedFile?.name
        ? selectedFile.name.replace(/\.[^.]+$/, "")
        : "OPI_Logs";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `${base}_detailed_pdfs.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 0);
      return true;
    } catch (err) {
      return fail(
        `Detailed Logs PDF export failed: ${String(
          err?.message || err,
        ).slice(0, 300)}`,
        err,
      );
    } finally {
      // Unmount the off-screen document so the next export starts clean, and
      // stop the spinner. Any failure stays visible via `pdfExportError`.
      setDetailedLogsDoc(null);
      setPdfExportStatus(null);
    }
  };

  // Export all three datasets to a single XLSX with separate sheets
  const exportAllToXLSX = async () => {
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook();

      // Unique sheet names (<= 31 chars, Excel-safe)
      const usedNames = new Set();
      const usedTableNames = new Set(
        (wb.model?.tables || []).map((tbl) => tbl.name),
      );
      const getUniqueSheetName = (name) => {
        let s = String(name || "Sheet")
          .replace(/[\\/*?[\]:]/g, " ")
          .trim();
        if (s.length > 31) s = s.slice(0, 31).trim();
        if (!s) s = "Sheet";
        let candidate = s;
        let i = 2;
        while (usedNames.has(candidate)) {
          const base = s.length > 28 ? s.slice(0, 28) : s;
          candidate = `${base}(${i++})`;
          if (candidate.length > 31) candidate = candidate.slice(0, 31);
        }
        usedNames.add(candidate);
        return candidate;
      };

      // Header + alternating row styling
      const styleHeader = (row) => {
        row.font = {
          name: "Calibri",
          size: 12,
          bold: true,
          color: { argb: "FFFFFFFF" },
        };
        row.alignment = { vertical: "middle", horizontal: "left" };
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4F81BD" },
          };
        });
      };
      const styleDataRows = (ws, start = 2) => {
        for (let r = start; r <= ws.rowCount; r++) {
          const row = ws.getRow(r);
          row.font = {
            name: "Calibri",
            size: 12,
            bold: false,
            color: { argb: "FF000000" },
          };
          const fillColor = r % 2 === 0 ? "FFB8CCE4" : "FFDCE6F1"; // alternate
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: fillColor },
            };
          });
        }
      };

      // Create a styled table sheet from an array of objects
      const addStyledTableSheet = (name, rows, explicitHeaders) => {
        const sheetName = getUniqueSheetName(name);
        const ws = wb.addWorksheet(sheetName);

        const data = Array.isArray(rows) ? rows : [];
        if (data.length === 0) {
          return ws;
        }

        const headers =
          explicitHeaders && explicitHeaders.length
            ? explicitHeaders
            : Object.keys(data[0]);

        // Build row matrix
        const matrix = data.map((r) =>
          headers.map((h) => {
            const v = r?.[h];
            return Array.isArray(v) ? JSON.stringify(v) : v;
          }),
        );

        // Add header + data rows
        ws.addRow(headers);
        matrix.forEach((vals) => ws.addRow(vals));

        // Style header and rows
        styleHeader(ws.getRow(1));
        styleDataRows(ws, 2);

        // Fit columns roughly
        const pxToExcelWidth = (px) => Math.max(10, Math.round(px / 7));
        headers.forEach((h, i) => {
          let max = h.length;
          matrix.forEach((row) => {
            const len = row[i] === null ? 0 : String(row[i]).length;
            if (len > max) max = len;
          });
          ws.getColumn(i + 1).width = Math.min(Math.max(12, max + 2), 120);
        });
        // Force wider Details column (~700px) on Compressed Logs sheet
        if (name === "Compressed Logs") {
          const detailsIdx = headers.indexOf("Details");
          if (detailsIdx !== -1) {
            ws.getColumn(detailsIdx + 1).width = pxToExcelWidth(700);
          }
        }

        // Add an Excel table for filtering/sorting (neutral stripes to keep our colors)
        const tableNameBase =
          sheetName.replace(/[^A-Za-z0-9_]/g, "_") || "Table";
        let tableName = tableNameBase;
        let t = 1;
        while (usedTableNames.has(tableName)) {
          tableName = `${tableNameBase}_${t++}`;
        }
        usedTableNames.add(tableName);
        ws.addTable({
          name: tableName,
          ref: "A1",
          headerRow: true,
          style: { theme: "TableStyleMedium2", showRowStripes: false },
          columns: headers.map((h) => ({ name: h, filterButton: true })),
          rows: matrix,
        });

        return ws;
      };

      // Helper to drop internal ID fields
      const OMIT_EXPORT_KEYS = new Set(["LanguageID", "ClientID"]);
      const prepareRows = (rows) =>
        Array.isArray(rows)
          ? rows.map((row) => {
              const out = {};
              Object.keys(row || {}).forEach((k) => {
                if (OMIT_EXPORT_KEYS.has(k)) return;
                const v = row[k];
                out[k] = Array.isArray(v) ? JSON.stringify(v) : v;
              });
              return out;
            })
          : [];

      // Main sheets
      addStyledTableSheet("Compressed Logs", prepareRows(csvCompressedData));
      addStyledTableSheet("Clean Logs", prepareRows(csvCleanData));
      addStyledTableSheet("Raw Logs", prepareRows(csvRawData));

      // Detailed Logs: per Account (Client), from Clean Logs
      const detailedHeaders = [
        "Requestor",
        "Account Name",
        "Date/Time",
        "Language",
        "Service Type",
        "Client Billable Duration",
        "Account Charge",
        "From Number",
        "Requestor Device Info",
        "Other Participants",
      ];

      const groupsByClient = new Map();
      (Array.isArray(csvCleanData) ? csvCleanData : []).forEach((row) => {
        let accName = String(row?.["Account Name"] ?? "").trim();
        if (!accName) accName = "Unknown";
        if (!groupsByClient.has(accName)) groupsByClient.set(accName, []);
        groupsByClient.get(accName).push(row);
      });

      groupsByClient.forEach((rows, clientName) => {
        const nameMap = {
          "CWS Global - HQ - Remote Services": "CWS HQ Remote Services",
          "CWS Global - Southwest - Houston": "CWS SW Houston",
          "CWS Global - Southwest - Dallas": "CWS SW Dallas",
        };
        const mapped = nameMap[clientName] || clientName;
        const data = rows.map((r) => {
          const out = {};
          detailedHeaders.forEach((h) => (out[h] = r?.[h] ?? ""));
          return out;
        });
        addStyledTableSheet(`Detailed Logs: ${mapped}`, data, detailedHeaders);
      });

      // Download
      const base = selectedFile?.name
        ? selectedFile.name.replace(/\.[^.]+$/, "")
        : "OPI_Logs";
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${base}_export.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 0);
    } catch (err) {
      console.error("ExcelJS export failed", err);
      showSnackbar(
        "Couldn't prepare the XLSX export — check your connection and try again.",
        theme,
        "error",
      );
    }
  };

  // Submit a single OPI entry using your API wrapper. Fill in the inner logic.
  const submitOPIEntry = async (entry, index) => {
    const language = overlayData.Languages.find(
      (l) => l["id"] === entry["LanguageID"],
    );

    const client = overlayData.Clients.find(
      (c) => c["id"] === entry["ClientID"],
    );

    const boostlingo = overlayData.Linguists.find(
      (l) => l["Name"] === "Boostlingo",
    );

    const requestor = client.Requestors.find(
      (r) => r["Name"] === entry["Requestor"],
    ) || { Name: entry["Requestor"] };

    const data = {
      Assignment: {
        id: 0,
        Date: date.format("YYYY-MM-DD"),
        ProjectNumber: Number(projectNumber) + index,
        CheckingDate: null,
        TimeStart: "00:00:00",
        TimeEnd: "00:00:00",
        ServiceCategory: "Interpretation",
        TotalDuration: "00:00:00",
        Duration: "00:00:00",
        ActualDuration: "00:00:00",
        Name: "Monthly On-Demand Phone Interpretation",
        Details: entry["Details"],
        InterpretationStatus: "Completed",
        AccountCharge: entry["Account Charge"],
        TotalCost: null,
        AdditionalCost: null,
        LinguistPay: null,
        Profit: entry["Account Charge"],
        Margin: 100,
        InterpretationType: "OPI",
        Mode: null,
        Address: "",
        MileageRoundtrip: 0,
        MileageCost: 0,
        ParkingFeesTolls: null,
        PreparationTime: "00:00:00",
        SchedulingTime: "00:00:00",
        ActualStart: "00:00:00",
        ActualEnd: "00:00:00",
        Paid: false,
        Rate: null,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: null,
        ContactPersonNumber: null,
      },
      Language: language,
      Client: client,
      Requestor: requestor,
      Linguist: boostlingo, //Get Boostlingo's ID
      Invoicer: null,
    };

    api
      .post("/services/interpretations/addNewInterpretation", {
        data: data,
      })
      .then((response) => {})
      .catch((err) => console.log(err));
  };

  const startProcessing = async () => {
    setIsProcessing(true);
    setProcessDone(false);
    setProcessPercent(0);

    const total = csvCompressedData.length || 0;
    if (total === 0) {
      setIsProcessing(false);
      setProcessDone(true);
      setProcessPercent(100);
      return;
    }

    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    await delay(250);

    for (let i = 0; i < total; i++) {
      const entry = csvCompressedData[i];
      try {
        await submitOPIEntry(entry, i + 1);
      } catch (e) {
        // Optionally collect/report submission errors.
        // For now, continue processing remaining entries.
      }
      const pct = Math.round(((i + 1) / total) * 100);

      await delay(15);

      setProcessPercent(pct);
    }

    setIsProcessing(false);
    setProcessDone(true);
    triggerPageRefresh("OPI Assignments has been added Sucessfully", theme);
  };

  const handleDownloadAndClose = async () => {
    // Mark the export as running (spinner + disabled controls) and clear a
    // previous failure before retrying.
    setPdfExportError(null);
    setPdfExportStatus("Preparing export…");
    try {
      await exportAllToXLSX();
      if (downloadDetailedLogs) {
        const ok = await exportDetailedPDFs();
        if (!ok) {
          // Keep the overlay open: the failure is shown inline next to the
          // button so the user can read it, retry, or close manually.
          return;
        }
      }
      setOverlayName("");
    } catch (err) {
      // Defensive guard — the export helpers handle their own errors; this
      // handler must never reject unhandled.
      console.error("Download and close failed", err);
      setPdfExportError(
        `Download failed: ${String(err?.message || err).slice(0, 300)}`,
      );
    } finally {
      setPdfExportStatus(null);
    }
  };

  const handleCloseWithoutDownload = () => {
    setOverlayName("");
  };

  const handleOpenErrorsDialog = () => {
    const content = (
      <Stack spacing={2}>
        {errorsList.map((error, index) => (
          <Box
            key={index}
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Typography variant="h5">{error}</Typography>
          </Box>
        ))}
      </Stack>
    );

    openDialog({
      title: "Upload Errors",
      message: null,
      customContent: content,
      secondaryAction: "Close",
      secondaryOnClick: closeDialog,
      theme: theme,
    });
  };

  // Autosize after data finishes loading and the grid has rendered rows
  /***
   * useEffect(() => {
    if (
      !isCSVProcessing &&
      dataRows.length > 0 &&
      dataColumns.length > 0 &&
      apiRef.current
    ) {
      const raf = requestAnimationFrame(() => {
        apiRef.current.autosizeColumns(autosizeOptions);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isCSVProcessing, dataRows, dataColumns]);
   */

  // Switch visible grid data when the selected sheet changes
  useEffect(() => {
    if (sheet === "Raw Logs") {
      if (csvRawData?.length) setRowsAndColumns(csvRawData);
    } else if (sheet === "Clean Logs") {
      if (csvCleanData?.length) setRowsAndColumns(csvCleanData);
    } else if (sheet === "Compressed Logs") {
      if (csvCompressedData?.length) setRowsAndColumns(csvCompressedData);
    }
  }, [sheet, csvRawData, csvCleanData, csvCompressedData, setRowsAndColumns]);

  const UploadLayout = () => {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleUploadClick}
        >
          Upload Logs File
        </Button>
      </Box>
    );
  };

  const CSVLayout = () => {
    return (
      <Box
        bgcolor={theme.palette.background.default}
        //mt="60px"
        //p="1rem"
        sx={{
          //position: "absolute",
          //inset: 0,
          //top: "auto", // Override the inset:0 for top
          width: "100%",
          height: "100%",
          display: "flex",
          minHeight: 0,
          flex: 1,
          alignItems: "stretch",
          justifyContent: "flex-start",
          textAlign: "left",
          flexDirection: "column",
          gap: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        {isProcessing || processDone ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {isProcessing && !processDone ? (
              <>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Adding {csvCompressedData.length} OPI entries to the
                  Interpretations List
                </Typography>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={processPercent}
                    size={80}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      color="text.secondary"
                    >
                      {`${Math.round(processPercent)}%`}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Successfully added {csvCompressedData.length} OPI entries to
                  the Interpretations List
                </Typography>
                <CheckCircleRoundedIcon
                  sx={{
                    fontSize: 80,
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                />
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  onClick={handleDownloadAndClose}
                  loading={Boolean(pdfExportStatus)}
                  disabled={Boolean(pdfExportStatus)}
                  startIcon={<FileDownloadRounded />}
                  loadingPosition="start"
                  sx={{ mb: 1 }}
                >
                  Download OPI Final Logs and Close
                </LoadingButton>
                {pdfExportStatus && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {pdfExportStatus}
                  </Typography>
                )}
                {pdfExportError && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mb: 1, maxWidth: 520 }}
                  >
                    {pdfExportError}
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={downloadDetailedLogs}
                      onChange={(e) =>
                        setDownloadDetailedLogs(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Download Detailed Logs ZIP"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleCloseWithoutDownload}
                    disabled={Boolean(pdfExportStatus)}
                  >
                    Close without downloading
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : (
          <DataGridResponsive
            editMode="row"
            loading={isCSVProcessing}
            apiRef={apiRef}
            pageSizeOptions={[20, 50, 100]}
            disableRowSelectionOnClick
            processRowUpdate={processRowUpdate}
            ignoreValueFormatterDuringExport
            getRowClassName={getRowClassName}
            getRowId={(row) => row.id}
            rows={dataRows}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            getCellClassName={(params) => {
              return params.value === "✘" ||
                params.value === "N/A" ||
                params.value === "0%" ||
                params.value === "null"
                ? "notAvailable"
                : "";
            }}
            mobile={{
              // Card mode (mobile) config — the desktop grid ignores this
              // prop (the wrapper drops it before rendering).
              // "Account Name" exists on all three sheets and is the anchor
              // of the whole OPI flow (client mapping, minimums, errors) →
              // card headline. "Account Charge" is the money value managers
              // verify → header chip (same string the desktop cell shows).
              // LanguageID / ClientID are already hidden through
              // columnVisibilityModel (the wrapper respects it). No
              // selection exists (disableRowSelectionOnClick, no
              // checkboxSelection) and a row click does nothing today, so a
              // card tap stays inert (no tap-to-open wiring).
              primaryField: "Account Name",
              badgeField: "Account Charge",
              hiddenFields: [],
              multilineFields: ["Details"],
              pageSize: 20,
              selectable: false,
            }}
            slots={{
              toolbar: CustomToolbar,
            }}
            //autosizeOptions={autosizeOptions}
            slotProps={{
              row: {
                //onContextMenu: handleContextMenu,
                //style: { cursor: "context-menu" },
              },
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
              toolbar: {
                showQuickFilter: true,
                printOptions: { disableToolbarButton: true },
                csvOptions: { disableToolbarButton: true },
                isProcessing,
                processDone,
                hasUnsavedRows,
                isSaving,
                saveChanges,
                discardChanges,
                exportAllToXLSX,
                exportDetailedPDFs,
              },
            }}
            ignoreDiacritics
            sx={{
              display: isProcessing || processDone ? "none" : "flex",
              flex: 1,
              minHeight: 0,
              height: "100%",
              width: "100%",
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
              // Ensure the internal virtual scroller can scroll vertically
              "& .MuiDataGrid-main": {
                height: "100%",
                minHeight: 0,
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowY: "auto !important",
              },
              "& .MuiDataGrid-virtualScrollerContent": {
                minHeight: "100%",
              },
              "& ::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.background.default,
                margin: "1rem",
              },
              "& ::-webkit-scrollbar-thumb": {
                borderRadius: "12px",
                backgroundColor: theme.palette.background.alt,
              },
              "& ::-webkit-scrollbar-thumb:hover": {
                borderRadius: "12px",
                backgroundColor: theme.palette.grey[600],
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: theme.palette.background.default,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-scrollbarFiller": {
                backgroundColor: theme.palette.background.default,
              },
              "& .MuiDataGrid-menuIconButton": {
                opacity: 1,
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-columnSeparator": {
                color: theme.palette.primary.main,
              },
              "& .MuiButton-root": {
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none !important",
                },
              [`& .${gridClasses.row}.row--removed`]: {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === "light") {
                    return "rgba(255, 170, 170, 0.3)";
                  }
                  return darken("rgba(255, 170, 170, 1)", 0.7);
                },
              },
              [`& .${gridClasses.row}.row--edited`]: {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === "light") {
                    return "rgba(255, 254, 176, 0.3)";
                  }
                  return darken("rgba(255, 254, 176, 1)", 0.6);
                },
              },
            }}
          />
        )}
      </Box>
    );
  };

  /***
   * Adjust the save button and the revert button in a way that the column is added one columns creation
   * ensure that things are being saved locally in a way that it also gets saved on the clean and the compressed versions
   *
   */

  // Header action — upload trigger (shown only while a staged file is idle).
  const headerActions =
    selectedFile && !isProcessing && !processDone ? (
      <IconButton
        onClick={handleUploadClick}
        size="large" // makes the button bigger
        sx={{ alignSelf: "center" }} // centers it vertically in the flex container
      >
        <UploadFileRounded fontSize="inherit" />{" "}
        {/* matches the button size */}
      </IconButton>
    ) : null;

  return (
    <Overlay
      id="interpretation-opi-upload"
      open={true}
      onClose={() => setOverlayName("")}
      title="Upload OPI Logs"
      maxWidthPx={1200}
      headerActions={headerActions}
      footer={renderFooterActions()}
      scrollable={!isMobile}
    >
      {/* Hidden file input - always in DOM so ref works */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {/* Off-screen host for the invoice-styled Detailed Logs documents. The
          ref is the document root (its direct children are the pages) —
          the capture contract of generateInvoicePdfBlob. Rendered only
          while an export is running. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: 0,
          left: "-99999px",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        {detailedLogsDoc && (
          <DetailedLogsDocument
            ref={detailedLogsRef}
            clientName={detailedLogsDoc.client}
            periodLabel={detailedLogsDoc.period}
            rows={detailedLogsDoc.rows}
          />
        )}
      </Box>

      {/* Content wrapper — flex column so the form (flex: 1) fills the body;
          the DataGrid keeps its own internal scroll. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          minHeight: 0,
        }}
      >

        <Box
          component="form"
          sx={{
            "::-webkit-scrollbar": {
              width: ".5rem",
            },
            "::-webkit-scrollbar-track": {
              background: theme.palette.background.default,
              m: "1rem",
            },
            "::-webkit-scrollbar-thumb": {
              borderRadius: "12px",
              background: theme.palette.background.alt,
            },
            display: "flex",
            //p: "1rem 2.3rem 1rem 2.5rem",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            width: "100%",
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.background.default,
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
              height: "90%",
            },
          }}
        >
          {selectedFile ? <CSVLayout /> : <UploadLayout />}
        </Box>
      </Box>
    </Overlay>
  );

  // Footer actions — hoisted (same pattern as InvoiceNew): the legacy footer
  // Box kept byte-identical, minus its own padding (the Overlay footer
  // supplies py/px).
  function renderFooterActions() {
    // Mobile (<600px): stack the footer in three rows so every element has
    // room — Sheet selector full-width on its own row, the errors status on
    // its own line, then Discard / Next as 40px icon buttons with tooltips
    // (the InterpretationNewEdit mobile footer recipe, right-aligned).
    // Desktop keeps the legacy single-row layout untouched below.
    if (isMobile) {
      const idle = !isProcessing && !processDone;
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 1,
          }}
        >
          {selectedFile && idle && (
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Sheet</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                size="small"
                id="demo-simple-select"
                value={sheet}
                label="Sheet"
                onChange={handleChange}
              >
                {sheets.map((sheet) => {
                  return (
                    <MenuItem key={sheet} value={sheet}>
                      {sheet}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {csvRawData.length > 0 && idle && (
            <Button
              variant="text"
              disabled={!errorsList.length}
              onClick={handleOpenErrorsDialog}
              sx={{
                color: errorsList.length
                  ? theme.palette.error[600]
                  : theme.palette.primary.main,
                cursor: errorsList.length ? "pointer" : "default",
                "&.Mui-disabled": {
                  color: theme.palette.primary.main,
                },
              }}
            >
              {errorsList.length
                ? `${errorsList.length} error${
                    errorsList.length > 1 ? "s" : ""
                  }`
                : "✓ No Errors"}
            </Button>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              visibility: idle ? "visible" : "hidden",
            }}
          >
            <Tooltip title="Discard" placement="top">
              <IconButton
                color="primary"
                onClick={() => setOverlayName("")}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.background.default,
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHover,
                  },
                }}
              >
                <DisabledByDefaultRoundedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Next" placement="top">
              <span>
                <IconButton
                  color="primary"
                  onClick={startProcessing}
                  disabled={
                    errorsList.length > 0 || isProcessing || processDone
                  }
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    color: "white",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.grey[200],
                    },
                  }}
                >
                  <NavigateNextRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      );
    }

    return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "35%",
              flexDirection: "row",
              minHeight: 40,
            }}
          >
            <FormControl
              sx={{
                width: "200px",
                visibility:
                  selectedFile && !isProcessing && !processDone
                    ? "visible"
                    : "hidden",
              }}
            >
              <InputLabel id="demo-simple-select-label">Sheet</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                size="small"
                id="demo-simple-select"
                value={sheet}
                label="Sheet"
                onChange={handleChange}
              >
                {sheets.map((sheet) => {
                  return <MenuItem value={sheet}>{sheet}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "30%",
              flexDirection: "row",
              minHeight: 40,
            }}
          >
            {csvRawData.length > 0 && (
              <Button
                variant="text"
                disabled={!errorsList.length}
                onClick={handleOpenErrorsDialog}
                sx={{
                  visibility:
                    !isProcessing && !processDone ? "visible" : "hidden",
                  color: errorsList.length
                    ? theme.palette.error[600]
                    : theme.palette.primary.main,
                  cursor: errorsList.length ? "pointer" : "default",
                  "&.Mui-disabled": {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {errorsList.length
                  ? `${errorsList.length} error${
                      errorsList.length > 1 ? "s" : ""
                    }`
                  : "✓ No Errors"}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "35%",
              flexDirection: "row",
              minHeight: 40,
            }}
          >
            <Box
              sx={{
                visibility:
                  !isProcessing && !processDone ? "visible" : "hidden",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>

              <Box p="0 .5rem" />

              <Button
                color="primary"
                variant="contained"
                size="large"
                endIcon={<NavigateNextRoundedIcon />}
                onClick={startProcessing}
                disabled={errorsList.length > 0 || isProcessing || processDone}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
    );
  }
};

function CustomToolbar({
  isProcessing,
  processDone,
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
  exportAllToXLSX,
  exportDetailedPDFs,
  isMobileToolbar,
}) {
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState(null);
  const [isExportingXlsx, setIsExportingXlsx] = React.useState(false);
  const theme = useTheme();

  // Card mode (mobile): the grid toolbar buttons collapse to 40×40 icon
  // buttons with tooltips (the standard mobile toolbar pattern), so nothing
  // is crammed. Disabled states are preserved exactly.
  const mobileToolbarButtonSx = {
    width: 40,
    height: 40,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "12px",
    "&:hover": {
      borderColor: theme.palette.primary.buttonHoverSelected,
      backgroundColor: theme.palette.primary.buttonHoverSelected,
      color: theme.palette.background.default,
    },
    "&.Mui-disabled": {
      color: theme.palette.grey[400],
      borderColor: theme.palette.divider,
    },
  };

  return (
    <GridToolbarContainer>
      <Box m="1" />
      {!isProcessing &&
        !processDone &&
        (isMobileToolbar ? (
          <>
            <Tooltip title="Save">
              <span>
                <IconButton
                  aria-label="Save"
                  disabled={!hasUnsavedRows || isSaving}
                  onClick={saveChanges}
                  sx={mobileToolbarButtonSx}
                >
                  {isSaving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Discard all changes">
              <span>
                <IconButton
                  aria-label="Discard all changes"
                  disabled={!hasUnsavedRows || isSaving}
                  onClick={discardChanges}
                  sx={mobileToolbarButtonSx}
                >
                  <RestoreIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <LoadingButton
              disabled={!hasUnsavedRows}
              loading={isSaving}
              onClick={saveChanges}
              startIcon={<SaveIcon />}
              loadingPosition="start"
            >
              <span>Save</span>
            </LoadingButton>
            <Button
              disabled={!hasUnsavedRows || isSaving}
              onClick={discardChanges}
              startIcon={<RestoreIcon />}
            >
              Discard all changes
            </Button>
          </>
        ))}
      <Box sx={{ flexGrow: 1 }} />
      {isMobileToolbar ? (
        <Tooltip title="Export">
          <IconButton
            aria-label="Export"
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            disabled={isExportingXlsx}
            sx={mobileToolbarButtonSx}
          >
            <FileDownloadRounded />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setExportMenuAnchor(e.currentTarget)}
          disabled={isExportingXlsx}
          startIcon={<FileDownloadRounded />}
        >
          Export
        </Button>
      )}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={async () => {
            setExportMenuAnchor(null);
            setIsExportingXlsx(true);
            try {
              await exportAllToXLSX();
            } finally {
              setIsExportingXlsx(false);
            }
          }}
        >
          XLSX Logs
        </MenuItem>
        <MenuItem
          onClick={() => {
            setExportMenuAnchor(null);
            exportDetailedPDFs();
          }}
        >
          Detailed PDFs
        </MenuItem>
      </Menu>
      <Box m="1" />
    </GridToolbarContainer>
  );
}

export default InterpretationOPIUpload;
